import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/config/firebase';
import { Business } from '@/types/business';
import { 
  Building2, 
  Users, 
  Calendar, 
  Bell, 
  TrendingUp, 
  DollarSign,
  Activity,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import AlertsPanel from '@/components/super/AlertsPanel';

interface DatabaseStats {
  businesses: number;
  users: number;
  classes: number;
  reservations: number;
  notifications: number;
}

interface BusinessWithStats extends Business {
  userCount?: number;
  classCount?: number;
}

const SuperAdminDashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [businesses, setBusinesses] = useState<BusinessWithStats[]>([]);
  const [stats, setStats] = useState<DatabaseStats | null>(null);
  const [recentPurchases, setRecentPurchases] = useState<any[]>([]);
  const [statsLoading, setStatsLoading] = useState(false);

  const loadData = async () => {
    try {
      // Cargar negocios
      const businessSnap = await getDocs(collection(db, 'businesses'));
      const businessList = businessSnap.docs.map(d => ({ 
        id: d.id, 
        ...(d.data() as any) 
      })) as BusinessWithStats[];
      setBusinesses(businessList);

      // Cargar compras recientes
      try {
        const purchasesQuery = query(
          collection(db, 'purchases'),
          orderBy('createdAt', 'desc'),
          limit(5)
        );
        const purchasesSnap = await getDocs(purchasesQuery);
        const purchases = purchasesSnap.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));
        setRecentPurchases(purchases);
      } catch (e) {
        console.log('No hay compras recientes');
      }

    } catch (e) {
      console.error('Error cargando datos:', e);
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const getDatabaseStatsFn = httpsCallable(functions, 'getDatabaseStats');
      const result = await getDatabaseStatsFn();
      setStats(result.data as DatabaseStats);
    } catch (e: any) {
      console.error('Error cargando estadísticas:', e);
      // Fallback: contar manualmente
      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        setStats({
          businesses: businesses.length,
          users: usersSnap.size,
          classes: 0,
          reservations: 0,
          notifications: 0,
        });
      } catch {
        toast.error('Error cargando estadísticas');
      }
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (businesses.length > 0 && !stats) {
      loadStats();
    }
  }, [businesses]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    await loadStats();
    setRefreshing(false);
    toast.success('Datos actualizados');
  };

  // Calcular métricas
  const activeBusinesses = businesses.filter(b => b.subscription?.status === 'active').length;
  const paidBusinesses = businesses.filter(b => 
    b.subscription?.plan && b.subscription.plan !== 'free'
  ).length;
  const freeBusinesses = businesses.filter(b => 
    !b.subscription?.plan || b.subscription.plan === 'free'
  ).length;

  // Agrupar negocios por mes de creación
  const businessesByMonth = businesses.reduce((acc, b) => {
    const date = b.createdAt?.toDate?.() || new Date(b.createdAt);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    acc[monthKey] = (acc[monthKey] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const sortedMonths = Object.keys(businessesByMonth).sort().slice(-6);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Panel Super Admin
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Vista global de la plataforma Reserberry
          </p>
        </div>
        <button 
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Negocios */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Negocios</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                {businesses.length}
              </p>
              <div className="flex items-center gap-2 mt-2 text-xs">
                <span className="text-green-500">{activeBusinesses} activos</span>
                <span className="text-gray-400">•</span>
                <span className="text-amber-500">{paidBusinesses} de pago</span>
              </div>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
              <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        {/* Usuarios */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Usuarios</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                {statsLoading ? (
                  <span className="inline-block w-16 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></span>
                ) : (
                  stats?.users || '—'
                )}
              </p>
              <p className="text-xs text-gray-400 mt-2">Todos los negocios</p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
              <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        {/* Clases */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Clases</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                {statsLoading ? (
                  <span className="inline-block w-16 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></span>
                ) : (
                  stats?.classes || '—'
                )}
              </p>
              <p className="text-xs text-gray-400 mt-2">Programadas</p>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
              <Calendar className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>

        {/* Notificaciones */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Notificaciones</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                {statsLoading ? (
                  <span className="inline-block w-16 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></span>
                ) : (
                  stats?.notifications || '—'
                )}
              </p>
              <p className="text-xs text-gray-400 mt-2">En el sistema</p>
            </div>
            <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
              <Bell className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Segunda fila de stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Distribución de planes */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4">
            Distribución de Planes
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Plan Gratuito</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{freeBusinesses}</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-gray-400 h-2 rounded-full" 
                style={{ width: `${(freeBusinesses / businesses.length) * 100}%` }}
              ></div>
            </div>
            
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-gray-600 dark:text-gray-300">Plan de Pago</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{paidBusinesses}</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-amber-500 h-2 rounded-full" 
                style={{ width: `${(paidBusinesses / businesses.length) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Crecimiento mensual */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Negocios por Mes
          </h3>
          <div className="flex items-end justify-between h-24 gap-1">
            {sortedMonths.map((month) => {
              const count = businessesByMonth[month];
              const maxCount = Math.max(...Object.values(businessesByMonth));
              const height = (count / maxCount) * 100;
              return (
                <div key={month} className="flex flex-col items-center flex-1">
                  <div 
                    className="w-full bg-amber-500 rounded-t transition-all"
                    style={{ height: `${height}%`, minHeight: '4px' }}
                  ></div>
                  <span className="text-[10px] text-gray-400 mt-1">
                    {month.split('-')[1]}
                  </span>
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Estado del sistema */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Estado del Sistema
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Firebase
              </span>
              <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full">
                Operativo
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Cloud Functions
              </span>
              <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full">
                Operativo
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Stripe
              </span>
              <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full">
                Operativo
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tablas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Últimos negocios */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Últimos Negocios Registrados
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Negocio
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Plan
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {businesses
                  .sort((a, b) => {
                    const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
                    const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
                    return dateB.getTime() - dateA.getTime();
                  })
                  .slice(0, 5)
                  .map(b => (
                    <tr key={b.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{b.name}</p>
                          <p className="text-xs text-gray-500">{b.adminEmail}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          b.subscription?.plan === 'free' 
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                            : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                        }`}>
                          {b.subscription?.plan || 'free'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          b.subscription?.status === 'active'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                        }`}>
                          {b.subscription?.status || 'activo'}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Últimas compras */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              Últimas Compras
            </h2>
          </div>
          <div className="p-4">
            {recentPurchases.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-20" />
                <p>No hay compras recientes</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentPurchases.map((purchase) => (
                  <div 
                    key={purchase.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">
                        {purchase.productName || purchase.productType}
                      </p>
                      <p className="text-xs text-gray-500">
                        {purchase.createdAt?.toDate?.().toLocaleDateString() || 'Fecha no disponible'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600 dark:text-green-400">
                        €{((purchase.amount || 0) / 100).toFixed(2)}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        purchase.status === 'completed'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-600'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600'
                      }`}>
                        {purchase.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Alertas del sistema - Dinámicas */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <AlertsPanel />
      </div>
    </div>
  );
};

export default SuperAdminDashboardPage;
